import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabaseClient";
import { SupabaseProfileRepository } from "@/repositories/SupabaseRepositories";
import { Profile } from "@/types";

const profileRepository = new SupabaseProfileRepository();

export function profileQueryKey(userId: string | undefined) {
  return ["profile", userId] as const;
}

// Fetches the logged-in user's profile via the repository layer and keeps it live
// via a Realtime subscription — moved out of AuthContext so auth/session state and
// profile data have separate, independently testable concerns.
export function useProfile(userId: string | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: profileQueryKey(userId),
    queryFn: () => profileRepository.getProfileById(userId as string),
    enabled: !!userId,
  });

  useEffect(() => {
    if (!userId) return;

    // Quando o webhook da Cakto confirmar o pagamento e atualizar
    // payment_status → 'confirmed', o frontend recebe o evento e
    // atualiza o cache do React Query automaticamente, liberando o
    // acesso sem o usuário precisar recarregar a página.
    const channel = supabase
      .channel(`profile-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "profiles",
          filter: `id=eq.${userId}`,
        },
        (payload) => {
          queryClient.setQueryData(profileQueryKey(userId), payload.new as Profile);
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);

  return query;
}
